import type { BudgetNoteRowDraft } from '../types/budgetNote';

/** 검정(라이트) / 흰색(다크) — DB·행 데이터에 저장되는 토큰 */
export const BUDGET_MARK_THEME_INK = '__theme_ink__';

const MARK_COLOR_FIELDS = ['cat1_color', 'cat2_color', 'cat3_color', 'cat4_color', 'memo_color'] as const;

/** 빨·주·노·초·파·남·보·회·검정(다크=흰) */
export const DEFAULT_BUDGET_NOTE_MARK_COLORS: readonly string[] = [
  '#e53935',
  '#fb8c00',
  '#fdd835',
  '#43a047',
  '#1e88e5',
  '#3949ab',
  '#8e24aa',
  '#9e9e9e',
  BUDGET_MARK_THEME_INK,
];

const DEFAULT_MARK_COLOR_SET = new Set(
  DEFAULT_BUDGET_NOTE_MARK_COLORS.map((c) => normalizeBudgetMarkHex(c))
);

export function normalizeBudgetMarkHex(hex: string): string {
  const t = hex.trim();
  if (t.toLowerCase() === BUDGET_MARK_THEME_INK) return BUDGET_MARK_THEME_INK;
  return t.toLowerCase();
}

export function isBudgetMarkThemeInk(hex: string): boolean {
  return normalizeBudgetMarkHex(hex) === BUDGET_MARK_THEME_INK;
}

export function isBudgetMarkDefaultColor(hex: string): boolean {
  return DEFAULT_MARK_COLOR_SET.has(normalizeBudgetMarkHex(hex));
}

/** 기본 색상 오른쪽에 표시할, 현재 행에서 사용 중인 커스텀 색상(첫 등장 순) */
export function collectUsedBudgetMarkCustomColors(rows: BudgetNoteRowDraft[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const row of rows) {
    for (const field of MARK_COLOR_FIELDS) {
      const raw = (row[field] ?? '').trim();
      if (!raw) continue;
      const n = normalizeBudgetMarkHex(raw);
      if (DEFAULT_MARK_COLOR_SET.has(n)) continue;
      if (seen.has(n)) continue;
      seen.add(n);
      out.push(raw);
    }
  }
  return out;
}

export function budgetMarkColorAriaLabel(hex: string): string {
  if (isBudgetMarkThemeInk(hex)) return '검정·흰색(테마)';
  return `색상 ${hex}`;
}

function parseCssHexRgb(hex: string): { r: number; g: number; b: number } | null {
  const t = hex.trim().replace(/^#/, '');
  if (!/^[0-9a-fA-F]{3}$|^[0-9a-fA-F]{6}$/.test(t)) return null;
  const full =
    t.length === 3
      ? t
          .split('')
          .map((c) => c + c)
          .join('')
      : t;
  const n = Number.parseInt(full, 16);
  if (!Number.isFinite(n)) return null;
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function relativeLuminance(r: number, g: number, b: number): number {
  const linear = [r, g, b].map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * linear[0] + 0.7152 * linear[1] + 0.0722 * linear[2];
}

/**
 * 메모 셀 전체 채우기용 배경·글자색.
 * 밝은 배경 → 검정 글자, 어두운 배경 → 흰 글자.
 * 테마 잉크(`__theme_ink__`)는 CSS로 라이트/다크를 맞추므로 null.
 */
export function budgetMarkMemoFillStyle(
  hex: string
): { backgroundColor: string; color: string } | null {
  const n = normalizeBudgetMarkHex(hex);
  if (!n || isBudgetMarkThemeInk(n)) return null;
  const rgb = parseCssHexRgb(n);
  if (!rgb) return null;
  const lum = relativeLuminance(rgb.r, rgb.g, rgb.b);
  const backgroundColor = n.startsWith('#') ? n : `#${n}`;
  return {
    backgroundColor,
    color: lum > 0.45 ? '#111111' : '#ffffff',
  };
}
